import React, { createContext, useContext, useMemo } from 'react'
import { isNativeApp, isAndroidApp, isiOSApp } from '../utils/nativeApp'

// Primary detection: a custom UA token injected by the native shell.
// Android:  WebSettings.setUserAgentString(default + " YumAndroid/<version>")
// iOS:      webView.customUserAgent = default + " YumiOS/<version>"
// Fallbacks (native bridges, legacy tokens) are kept only for safety.
const UA_ANDROID = /\bYumAndroid\b/i
const UA_IOS = /\bYumiOS\b/i
const UA_LEGACY = /\bYumExpress\b/i

const detectWebView = () => {
  if (typeof window === 'undefined') return false
  const ua = navigator.userAgent || ''

  // 1. Custom UA tokens (preferred)
  if (UA_ANDROID.test(ua) || UA_IOS.test(ua) || UA_LEGACY.test(ua)) return true

  // 2. Native JS bridge present (legacy builds without custom UA)
  if (isNativeApp()) return true

  return false
}

// Returns 'android' | 'ios' | 'web'
const detectPlatform = () => {
  if (typeof window === 'undefined') return 'web'
  const ua = navigator.userAgent || ''

  // Custom UA tokens (preferred)
  if (UA_ANDROID.test(ua)) return 'android'
  if (UA_IOS.test(ua)) return 'ios'

  // Native bridge fallback
  if (isAndroidApp()) return 'android'
  if (isiOSApp()) return 'ios'

  return 'web'
}

const AppModeContext = createContext({ isAppMode: false, platform: 'web' })

export const AppModeProvider = ({ children }) => {
  const value = useMemo(() => {
    const platform = detectPlatform()
    return {
      isAppMode: detectWebView(),
      platform,
      isAndroid: platform === 'android',
      isIOS: platform === 'ios',
      isWeb: platform === 'web'
    }
  }, [])

  return <AppModeContext.Provider value={value}>{children}</AppModeContext.Provider>
}

export const useAppMode = () => useContext(AppModeContext)
